import type { Book, Volume, Chapter, Setting, Faction, Character, Item, UserPreference, AIPromptTemplate, Conversation } from '../types';

interface BookCacheEntry {
  volumes: Volume[] | null;
  chaptersByVolume: Map<string, Chapter[]> | null;
  chapterContents: Map<string, string> | null;
  settings: Setting[] | null;
  outline: string | null;
  factions: Faction[] | null;
  characters: Character[] | null;
  items: Item[] | null;
  aiSessionsNormal: Conversation[] | null;
}

class DataCacheImpl {
  private _books: Book[] | null = null;
  private _deletedBooks: Book[] | null = null;
  private _aiPromptTemplates: AIPromptTemplate[] | null = null;
  private _userPreferences: UserPreference[] | null = null;
  private _bookCache = new Map<string, BookCacheEntry>();

  private ensureBook(bookId: string): BookCacheEntry {
    if (!this._bookCache.has(bookId)) {
      this._bookCache.set(bookId, {
        volumes: null, chaptersByVolume: null, chapterContents: null,
        settings: null, outline: null, factions: null, characters: null,
        items: null, aiSessionsNormal: null,
      });
    }
    return this._bookCache.get(bookId)!;
  }

  // ===== Getters =====

  get books(): Book[] | null { return this._books; }
  set books(v: Book[] | null) { this._books = v; }

  get deletedBooks(): Book[] | null { return this._deletedBooks; }
  set deletedBooks(v: Book[] | null) { this._deletedBooks = v; }

  get aiPromptTemplates(): AIPromptTemplate[] | null { return this._aiPromptTemplates; }
  set aiPromptTemplates(v: AIPromptTemplate[] | null) { this._aiPromptTemplates = v; }

  get userPreferences(): UserPreference[] | null { return this._userPreferences; }
  set userPreferences(v: UserPreference[] | null) { this._userPreferences = v; }

  getVolumes(bookId: string): Volume[] | null { return this.ensureBook(bookId).volumes; }
  setVolumes(bookId: string, v: Volume[] | null): void { this.ensureBook(bookId).volumes = v; }

  getChapters(volumeId: string, bookId: string): Chapter[] | null {
    const entry = this._bookCache.get(bookId);
    if (!entry || !entry.chaptersByVolume) return null;
    return entry.chaptersByVolume.get(volumeId) ?? null;
  }
  getChaptersByVolumeRaw(bookId: string): Record<string, Chapter[]> | null {
    const entry = this._bookCache.get(bookId);
    if (!entry || !entry.chaptersByVolume) return null;
    const result: Record<string, Chapter[]> = {};
    entry.chaptersByVolume.forEach((v, k) => { result[k] = v; });
    return result;
  }
  getChapterContentsRaw(bookId: string): Map<string, string> | null {
    return this._bookCache.get(bookId)?.chapterContents ?? null;
  }
  setChaptersByVolume(bookId: string, map: Map<string, Chapter[]> | null): void {
    this.ensureBook(bookId).chaptersByVolume = map;
  }

  getChapterContent(chapterId: string, bookId: string): string | null {
    const entry = this._bookCache.get(bookId);
    if (!entry || !entry.chapterContents) return null;
    return entry.chapterContents.get(chapterId) ?? null;
  }
  setChapterContents(bookId: string, map: Map<string, string> | null): void {
    this.ensureBook(bookId).chapterContents = map;
  }

  getSettings(bookId: string): Setting[] | null { return this.ensureBook(bookId).settings; }
  setSettings(bookId: string, v: Setting[] | null): void { this.ensureBook(bookId).settings = v; }

  getOutline(bookId: string): string | null { return this.ensureBook(bookId).outline; }
  setOutline(bookId: string, v: string | null): void { this.ensureBook(bookId).outline = v; }

  getFactions(bookId: string): Faction[] | null { return this.ensureBook(bookId).factions; }
  setFactions(bookId: string, v: Faction[] | null): void { this.ensureBook(bookId).factions = v; }

  getCharacters(bookId: string): Character[] | null { return this.ensureBook(bookId).characters; }
  setCharacters(bookId: string, v: Character[] | null): void { this.ensureBook(bookId).characters = v; }

  getItems(bookId: string): Item[] | null { return this.ensureBook(bookId).items; }
  setItems(bookId: string, v: Item[] | null): void { this.ensureBook(bookId).items = v; }

  getAISessions(bookId: string): Conversation[] | null { return this.ensureBook(bookId).aiSessionsNormal; }
  setAISessions(bookId: string, v: Conversation[] | null): void { this.ensureBook(bookId).aiSessionsNormal = v; }

  // ===== 就地更新（保存/摘要编辑后保持缓存新鲜） =====

  /** 就地替换某章节元数据（原地修改数组元素，共享该数组的状态引用同时生效） */
  patchChapter(bookId: string, chapter: Chapter): void {
    const entry = this._bookCache.get(bookId);
    const list = entry?.chaptersByVolume?.get(chapter.volume_id);
    if (!list) return;
    const idx = list.findIndex(c => c.id === chapter.id);
    if (idx >= 0) list[idx] = chapter;
  }

  /** 就地更新某章节正文缓存 */
  patchChapterContent(bookId: string, chapterId: string, content: string): void {
    const entry = this._bookCache.get(bookId);
    if (!entry || !entry.chapterContents) return;
    entry.chapterContents.set(chapterId, content);
  }

  // ===== Invalidation =====

  invalidateBook(bookId: string): void {
    this._bookCache.delete(bookId);
  }

  invalidateBooks(): void {
    this._books = null;
    this._deletedBooks = null;
  }

  invalidateVolumes(bookId: string): void {
    const entry = this._bookCache.get(bookId);
    if (entry) {
      entry.volumes = null;
      entry.chaptersByVolume = null;
    }
  }

  invalidateChapters(bookId: string): void {
    const entry = this._bookCache.get(bookId);
    if (entry) {
      entry.chaptersByVolume = null;
      entry.chapterContents = null;
    }
  }

  invalidateSettings(bookId: string): void {
    const entry = this._bookCache.get(bookId);
    if (entry) entry.settings = null;
  }

  invalidateOutline(bookId: string): void {
    const entry = this._bookCache.get(bookId);
    if (entry) entry.outline = null;
  }

  invalidateFactions(bookId: string): void {
    const entry = this._bookCache.get(bookId);
    if (entry) entry.factions = null;
  }

  invalidateCharacters(bookId: string): void {
    const entry = this._bookCache.get(bookId);
    if (entry) entry.characters = null;
  }

  invalidateItems(bookId: string): void {
    const entry = this._bookCache.get(bookId);
    if (entry) entry.items = null;
  }

  invalidateAISessions(bookId: string): void {
    const entry = this._bookCache.get(bookId);
    if (entry) entry.aiSessionsNormal = null;
  }

  invalidateAll(): void {
    this._books = null;
    this._deletedBooks = null;
    this._aiPromptTemplates = null;
    this._userPreferences = null;
    this._bookCache.clear();
    try { sessionStorage.removeItem(DataCacheImpl.SNAPSHOT_KEY); } catch { /* ignore */ }
  }

  isBooted(): boolean {
    return this._books !== null;
  }

  // ===== sessionStorage 快照（刷新时跳过全量网络请求） =====

  private static SNAPSHOT_KEY = 'dc_snapshot';

  snapshot(): string {
    const bookEntries: Record<string, Record<string, unknown>> = {};
    this._bookCache.forEach((entry, bookId) => {
      const serialized: Record<string, unknown> = {};
      if (entry.volumes !== null) serialized.v = entry.volumes;
      if (entry.chaptersByVolume !== null) {
        const cv: Record<string, Chapter[]> = {};
        entry.chaptersByVolume.forEach((chaps, volId) => { cv[volId] = chaps; });
        serialized.cv = cv;
      }
      if (entry.chapterContents !== null) {
        const cc: Record<string, string> = {};
        entry.chapterContents.forEach((content, chId) => { cc[chId] = content; });
        serialized.cc = cc;
      }
      if (entry.settings !== null) serialized.se = entry.settings;
      if (entry.outline !== null) serialized.ol = entry.outline;
      if (entry.factions !== null) serialized.fa = entry.factions;
      if (entry.characters !== null) serialized.ch = entry.characters;
      if (entry.items !== null) serialized.it = entry.items;
      if (entry.aiSessionsNormal !== null) serialized.as = entry.aiSessionsNormal;
      bookEntries[bookId] = serialized;
    });

    const data = {
      b: this._books,
      db: this._deletedBooks,
      pt: this._aiPromptTemplates,
      up: this._userPreferences,
      bc: bookEntries,
    };
    return JSON.stringify(data);
  }

  restore(json: string): boolean {
    try {
      const data = JSON.parse(json);
      if (!data || !data.b) return false;
      this._books = data.b;
      this._deletedBooks = data.db ?? null;
      this._aiPromptTemplates = data.pt ?? null;
      this._userPreferences = data.up ?? null;
      this._bookCache.clear();
      const bookEntries = data.bc ?? {};
      for (const [bookIdStr, entry] of Object.entries(bookEntries)) {
        const bookId = bookIdStr;
        const e = entry as Record<string, unknown>;
        const chaptersByVolume = e.cv
          ? new Map<string, Chapter[]>(Object.entries(e.cv as Record<string, Chapter[]>).map(([k, v]) => [k, v]))
          : null;
        const chapterContents = e.cc
          ? new Map<string, string>(Object.entries(e.cc as Record<string, string>).map(([k, v]) => [k, v]))
          : null;
        this._bookCache.set(bookId, {
          volumes: (e.v as Volume[]) ?? null,
          chaptersByVolume,
          chapterContents,
          settings: (e.se as Setting[]) ?? null,
          outline: (e.ol as string) ?? null,
          factions: (e.fa as Faction[]) ?? null,
          characters: (e.ch as Character[]) ?? null,
          items: (e.it as Item[]) ?? null,
          aiSessionsNormal: (e.as as Conversation[]) ?? null,
        });
      }
      return true;
    } catch {
      return false;
    }
  }

  saveSnapshot(): void {
    try {
      sessionStorage.setItem(DataCacheImpl.SNAPSHOT_KEY, this.snapshot());
    } catch { /* sessionStorage 满了则静默跳过 */ }
  }

  restoreSnapshot(): boolean {
    try {
      const json = sessionStorage.getItem(DataCacheImpl.SNAPSHOT_KEY);
      if (!json) return false;
      return this.restore(json);
    } catch {
      return false;
    }
  }
}

export const dataCache = new DataCacheImpl();
